import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Calendar, ArrowLeft, Check, MessageCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import PageLayout from "@/components/layout/PageLayout";
import InquiryDialog from "@/components/InquiryDialog";
import TourCard from "@/components/TourCard";
import Seo from "@/components/Seo";
import { breadcrumbSchema } from "@/components/JsonLd";

export default function DestinationDetail() {
  const { slug } = useParams();
  const [dest, setDest] = useState(null);
  const [relatedTours, setRelatedTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/destinations/${slug}`);
        setDest(data);
        // Fetch all tours then filter to ones whose locations include the destination name
        const { data: tours } = await api.get("/tours");
        const matches = tours.filter((t) =>
          t.locations?.some((loc) =>
            loc.toLowerCase().includes(data.name.toLowerCase()) ||
            data.name.toLowerCase().includes(loc.toLowerCase())
          )
        );
        setRelatedTours(matches.slice(0, 6));
      } catch (_) {
        setDest(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center text-foreground/60">Loading…</div>
      </PageLayout>
    );
  }

  if (!dest) {
    return (
      <PageLayout>
        <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
          <h1 className="font-serif text-5xl">Destination not found.</h1>
          <Link to="/destinations" className="mt-6 btn-primary">Browse all destinations</Link>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Seo
        title={`${dest.name} — ${dest.country} travel guide`}
        description={dest.summary?.slice(0, 160) || `Visit ${dest.name} in ${dest.country} with Tour Trips India Holidays — best time to visit, highlights and curated tours.`}
        image={dest.image_url}
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Destinations", path: "/destinations" },
          { name: dest.name, path: `/destinations/${dest.slug}` },
        ])}
      />
      <section className="relative h-[70vh] min-h-[500px] flex items-end" data-testid="destination-hero">
        <div className="absolute inset-0">
          <img src={dest.image_url} alt={dest.name} className="w-full h-full object-cover animate-slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/30 to-black/85" />
        </div>
        <div className="container-tti relative z-10 pb-20 pt-32">
          <Link
            to="/destinations"
            className="inline-flex items-center gap-2 text-white/80 text-sm hover:text-marigold mb-8"
            data-testid="back-to-destinations"
          >
            <ArrowLeft className="w-4 h-4" /> All Destinations
          </Link>
          <div className="text-marigold text-[10px] uppercase tracking-[0.3em] mb-3">
            {dest.country} · {dest.region}
          </div>
          <h1 className="font-serif text-5xl md:text-7xl text-white tracking-tighter leading-[0.95] max-w-4xl">
            {dest.name}
          </h1>
          <p className="text-marigold italic font-serif text-xl md:text-2xl mt-4">{dest.tagline}</p>
          <div className="flex flex-wrap items-center gap-5 mt-8 text-sm text-white/85">
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-marigold" /> {dest.country}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-marigold" /> Best time: {dest.best_time}
            </span>
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="destination-content">
        <div className="container-tti grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 space-y-12">
            <div>
              <div className="eyebrow mb-3">About</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-5">An introduction to {dest.name}</h2>
              <p className="text-foreground/80 text-[17px] leading-relaxed font-light">{dest.summary}</p>
            </div>

            {dest.highlights?.length > 0 && (
              <div>
                <div className="eyebrow mb-3">Don't miss</div>
                <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-6">Highlights of {dest.name}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {dest.highlights.map((h, i) => (
                    <div key={i} className="flex items-start gap-3 bg-cream-100 p-4 rounded-sm">
                      <div className="w-6 h-6 rounded-sm bg-saffron text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-[15px] font-light text-foreground/85 leading-snug">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-32 bg-cream-100 border border-cream-200 rounded-sm p-8 space-y-6">
              <div>
                <div className="eyebrow mb-2">Plan a trip</div>
                <h3 className="font-serif text-3xl text-deepred leading-tight">
                  Visit {dest.name} with TTI Holidays.
                </h3>
              </div>
              <p className="text-sm text-foreground/70 font-light">
                Our curator will design a custom itinerary built around {dest.name}, with the right hotels, guides, and pace for your party.
              </p>
              <button onClick={() => setInquiryOpen(true)} className="btn-primary w-full" data-testid="destination-inquire">
                Inquire about {dest.name}
              </button>
              <a
                href="https://wa.me/917017798524"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline w-full"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Us
              </a>
              <div className="text-xs text-center text-foreground/60 border-t border-cream-200 pt-4">
                Best time to visit: <strong>{dest.best_time}</strong>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {relatedTours.length > 0 && (
        <section className="py-24 bg-cream-100" data-testid="related-tours">
          <div className="container-tti">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
              <div>
                <div className="eyebrow mb-3">Curated journeys</div>
                <h2 className="font-serif text-4xl tracking-tight">Tours featuring {dest.name}</h2>
              </div>
              <Link to="/tours" className="btn-ghost group" data-testid="view-all-tours-link">
                View all tours <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedTours.map((t) => <TourCard key={t.slug} tour={t} />)}
            </div>
          </div>
        </section>
      )}

      <InquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} defaultTour={`Custom ${dest.name} trip`} />
    </PageLayout>
  );
}
